// Jianpu Studio core — parser, degree math, timing, grading, quantizer.
//
// Everything here is pure and key-agnostic: songs live in movable-do scale
// degrees (the EasyPlay 1s transposes itself, so button 1 is always "do").
// The plain-text format is specified in docs/jianpu-format.md at the repo
// root; this module is its reference implementation.

export const MAJOR_SCALE_SEMIS = [0, 2, 4, 5, 7, 9, 11];

export const DEFAULT_KEY = "C";
export const DEFAULT_TEMPO = 90;
export const DEFAULT_TIME = { num: 4, den: 4 };

// ---------------------------------------------------------------------------
// Key / degree / MIDI math
// ---------------------------------------------------------------------------

const PITCH_CLASS = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

// Parse "1=C", "1=Bb", "F#", "c" → canonical key name like "C", "Bb", "F#".
// Returns null for anything unrecognizable.
export function parseKey(raw) {
  if (!raw) return null;
  const m = String(raw)
    .trim()
    .match(/^(?:1\s*=\s*)?([A-Ga-g])\s*(#|b)?$/);
  if (!m) return null;
  return m[1].toUpperCase() + (m[2] || "");
}

// Tonic MIDI note for a key name. Kept in the octave G3..F#4 so playback sits
// in a comfortable range regardless of key.
export function keyTonicMidi(key) {
  const parsed = parseKey(key) || DEFAULT_KEY;
  const letter = parsed[0];
  const acc = parsed[1] === "#" ? 1 : parsed[1] === "b" ? -1 : 0;
  const pc = (PITCH_CLASS[letter] + acc + 12) % 12;
  // G3 (55) through F#4 (66).
  return 55 + ((pc - 7 + 12) % 12);
}

// Degree (1-7) + octave offset + accidental → absolute MIDI note.
export function degreeToMidi(tonicMidi, degree, octave = 0, accidental = 0) {
  return tonicMidi + MAJOR_SCALE_SEMIS[degree - 1] + accidental + octave * 12;
}

// Chromatic offset from tonic → movable-do spelling. Sharps are preferred for
// out-of-scale notes (#1 #2 #4 #5 #6), matching how the EasyPlay's gray
// buttons sit "between" the numbered ones.
const CHROMA_TO_DEGREE = [
  { degree: 1, accidental: 0 },
  { degree: 1, accidental: 1 },
  { degree: 2, accidental: 0 },
  { degree: 2, accidental: 1 },
  { degree: 3, accidental: 0 },
  { degree: 4, accidental: 0 },
  { degree: 4, accidental: 1 },
  { degree: 5, accidental: 0 },
  { degree: 5, accidental: 1 },
  { degree: 6, accidental: 0 },
  { degree: 6, accidental: 1 },
  { degree: 7, accidental: 0 },
];

// Canonical string key for a button/note position, accidental-aware:
// "1:0" is low do, "#2:1" is the small button between 2' and 3'. Used to
// match what's held on the instrument against grid buttons and chord shapes.
export function noteKey({ degree, octave = 0, accidental = 0 }) {
  return `${accidental === 1 ? "#" : accidental === -1 ? "b" : ""}${degree}:${octave}`;
}

// Absolute MIDI note → { degree, octave, accidental } relative to a tonic.
export function midiToDegree(midi, tonicMidi) {
  const offset = midi - tonicMidi;
  const octave = Math.floor(offset / 12);
  const chroma = ((offset % 12) + 12) % 12;
  const { degree, accidental } = CHROMA_TO_DEGREE[chroma];
  return { degree, octave, accidental };
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

const HEADER_FIELDS = ["title", "key", "time", "tempo"];

const NOTE_RE = /^(#|b)?([0-7])('+|,+)?(_|=)?(\.)?$/;

function makeError(line, message, warning = false) {
  return { line, message, warning };
}

// Parse the plain-text jianpu format. Never throws: returns
//   { song, errors }
// where errors is a list of { line, message, warning } (warning: true for
// soft issues like bar-length mismatches) and song is always usable:
//   {
//     title, key, time: {num, den}, tempo,
//     notes: [{ kind: 'note'|'rest', degree, octave, accidental, beats,
//               startBeat, bar, line,
//               render: { underlines, dotted, dashes } }],
//     barCount, totalBeats, beatsPerBar,
//   }
export function parseJianpu(text) {
  const errors = [];
  const song = {
    title: "",
    key: DEFAULT_KEY,
    time: { ...DEFAULT_TIME },
    tempo: DEFAULT_TEMPO,
    notes: [],
    barCount: 0,
    totalBeats: 0,
    beatsPerBar: 4,
  };

  const lines = String(text || "").split("\n");
  let inHeader = true;
  let bar = 0;
  let startBeat = 0;
  let barStartBeat = 0;
  const barSums = []; // beats per closed bar, for length warnings

  const closeBar = () => {
    barSums.push(startBeat - barStartBeat);
    barStartBeat = startBeat;
    bar += 1;
  };

  for (let i = 0; i < lines.length; i++) {
    const lineNo = i + 1;
    const raw = lines[i];
    const line = raw.trim();
    if (!line || line.startsWith("//")) continue;

    const header = inHeader && line.match(/^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.*)$/);
    if (header) {
      const field = header[1].toLowerCase();
      const value = header[2].trim();
      if (!HEADER_FIELDS.includes(field)) {
        errors.push(makeError(lineNo, `Unknown header field "${header[1]}"`));
      } else if (field === "title") {
        song.title = value;
      } else if (field === "key") {
        const key = parseKey(value);
        if (key) song.key = key;
        else errors.push(makeError(lineNo, `Can't read key "${value}" — try "1=C" or "1=Bb"`));
      } else if (field === "time") {
        const m = value.match(/^(\d{1,2})\s*\/\s*(\d{1,2})$/);
        if (m && Number(m[1]) > 0 && [2, 4, 8].includes(Number(m[2]))) {
          song.time = { num: Number(m[1]), den: Number(m[2]) };
        } else {
          errors.push(makeError(lineNo, `Can't read time signature "${value}" — try "4/4" or "3/4"`));
        }
      } else if (field === "tempo") {
        const bpm = Number(value);
        if (Number.isFinite(bpm) && bpm >= 20 && bpm <= 300) song.tempo = bpm;
        else errors.push(makeError(lineNo, `Tempo should be a number of BPM between 20 and 300`));
      }
      continue;
    }

    // First non-header line switches to music mode for the rest of the file.
    inHeader = false;

    const tokens = line.replace(/\|/g, " | ").split(/\s+/).filter(Boolean);
    for (const token of tokens) {
      if (token === "|") {
        // Ignore a barline before any notes (e.g. leading "|").
        if (song.notes.length === 0 && startBeat === 0 && bar === 0 && startBeat === barStartBeat)
          continue;
        closeBar();
        continue;
      }
      if (token === "-") {
        const prev = song.notes[song.notes.length - 1];
        if (!prev) {
          errors.push(makeError(lineNo, `"-" extends the previous note, but there isn't one yet`));
          continue;
        }
        prev.beats += 1;
        prev.render.dashes += 1;
        startBeat += 1;
        continue;
      }
      const m = token.match(NOTE_RE);
      if (!m) {
        errors.push(makeError(lineNo, `Can't read "${token}" — see the format guide`));
        continue;
      }
      const [, accRaw, degreeRaw, octRaw, durRaw, dotRaw] = m;
      const degree = Number(degreeRaw);
      const isRest = degree === 0;
      if (isRest && (accRaw || octRaw)) {
        errors.push(makeError(lineNo, `Rests (0) can't have accidentals or octave marks: "${token}"`, true));
      }
      const underlines = durRaw === "_" ? 1 : durRaw === "=" ? 2 : 0;
      let beats = underlines === 1 ? 0.5 : underlines === 2 ? 0.25 : 1;
      if (dotRaw) beats *= 1.5;
      const octave = octRaw ? (octRaw[0] === "'" ? octRaw.length : -octRaw.length) : 0;
      song.notes.push({
        kind: isRest ? "rest" : "note",
        degree: isRest ? 0 : degree,
        octave: isRest ? 0 : octave,
        accidental: isRest ? 0 : accRaw === "#" ? 1 : accRaw === "b" ? -1 : 0,
        beats,
        startBeat,
        bar,
        line: lineNo,
        render: { underlines, dotted: Boolean(dotRaw), dashes: 0 },
      });
      startBeat += beats;
    }
  }

  // Close the final bar if it has content.
  if (startBeat > barStartBeat) closeBar();

  song.barCount = bar;
  song.totalBeats = startBeat;
  // Beats are quarter notes throughout, so a 6/8 bar is 3 beats.
  song.beatsPerBar = song.time.num * (4 / song.time.den);

  barSums.forEach((sum, idx) => {
    // Pickup (first) and final bars are allowed to run short.
    const isEdge = idx === 0 || idx === barSums.length - 1;
    if (sum !== song.beatsPerBar && !(isEdge && sum < song.beatsPerBar)) {
      errors.push(
        makeError(
          barLine(song, idx),
          `Bar ${idx + 1} has ${trimNum(sum)} beat${sum === 1 ? "" : "s"} but ${song.time.num}/${song.time.den} wants ${trimNum(song.beatsPerBar)}`,
          true
        )
      );
    }
  });

  return { song, errors };
}

function barLine(song, barIdx) {
  const note = song.notes.find((n) => n.bar === barIdx);
  return note ? note.line : 1;
}

function trimNum(n) {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

// ---------------------------------------------------------------------------
// Serialization (used by the MIDI-record quantizer and tests)
// ---------------------------------------------------------------------------

// { degree, octave, accidental } → the pitch part of a token, e.g. "#4'".
export function serializePitch({ degree, octave = 0, accidental = 0 }) {
  const acc = accidental === 1 ? "#" : accidental === -1 ? "b" : "";
  const oct = octave > 0 ? "'".repeat(octave) : octave < 0 ? ",".repeat(-octave) : "";
  return `${acc}${degree}${oct}`;
}

// Representable single-note durations, longest first. Longer values are a
// base token plus "-" dash tokens.
const DURATIONS = [
  { beats: 1.5, suffix: "." },
  { beats: 1, suffix: "" },
  { beats: 0.75, suffix: "_." },
  { beats: 0.5, suffix: "_" },
  { beats: 0.25, suffix: "=" },
];

// beats → array of tokens for one note (or rest), e.g. 3 → ["1", "-", "-"].
// Snaps to the nearest representable duration.
export function beatsToTokens(pitch, beats) {
  const tokens = [];
  let extra = 0;
  if (beats >= 2) {
    extra = Math.floor(beats - 1);
    beats -= extra;
  }
  let best = DURATIONS[DURATIONS.length - 1];
  for (const d of DURATIONS) {
    if (Math.abs(d.beats - beats) < Math.abs(best.beats - beats)) best = d;
  }
  tokens.push(`${pitch}${best.suffix}`);
  for (let i = 0; i < extra; i++) tokens.push("-");
  return tokens;
}

// ---------------------------------------------------------------------------
// Timing — song → scheduled events
// ---------------------------------------------------------------------------

// Flatten a parsed song into playable events:
//   [{ index, kind, degree, octave, accidental, midi|null, startMs, durMs }]
// index is the position in song.notes. Rests are included (midi: null) so a
// player can show them pass by; filter kind === "note" for audio/scoring.
export function songEvents(song, opts = {}) {
  const bpm = opts.bpm || song.tempo || DEFAULT_TEMPO;
  const tonicMidi = opts.tonicMidi ?? keyTonicMidi(song.key);
  const beatMs = 60000 / bpm;
  return song.notes.map((n, index) => ({
    index,
    kind: n.kind,
    degree: n.degree,
    octave: n.octave,
    accidental: n.accidental,
    midi: n.kind === "note" ? degreeToMidi(tonicMidi, n.degree, n.octave, n.accidental) : null,
    startMs: n.startBeat * beatMs,
    durMs: n.beats * beatMs,
  }));
}

// ---------------------------------------------------------------------------
// Play-along grading
// ---------------------------------------------------------------------------

// Match one incoming note against the song's events. Degrees are compared,
// not raw MIDI numbers — the caller maps the instrument's notes through
// midiToDegree with whatever tonic the transpose dial is set to.
//
// events: from songEvents; matched: Set of already-hit event indexes;
// played: { degree, octave, accidental }; timeMs: when it was played on the
// song's clock. Returns the matched event index, or -1 for a stray note.
export function matchNote(events, matched, played, timeMs, windowMs = 320) {
  let best = -1;
  let bestDist = Infinity;
  for (const ev of events) {
    if (ev.kind !== "note" || matched.has(ev.index)) continue;
    const dist = Math.abs(ev.startMs - timeMs);
    if (dist > windowMs) continue;
    if (
      ev.degree === played.degree &&
      ev.octave === played.octave &&
      ev.accidental === played.accidental &&
      dist < bestDist
    ) {
      best = ev.index;
      bestDist = dist;
    }
  }
  return best;
}

// Grade a whole take: playedNotes = [{ degree, octave, accidental, timeMs }].
// Returns { total, hits, misses, extras, accuracy, avgOffsetMs, statuses }
// where statuses maps event index → "hit" | "miss" for note events.
export function gradeTake(events, playedNotes, windowMs = 320) {
  const matched = new Set();
  const offsets = [];
  let extras = 0;
  const sorted = [...playedNotes].sort((a, b) => a.timeMs - b.timeMs);
  for (const p of sorted) {
    const idx = matchNote(events, matched, p, p.timeMs, windowMs);
    if (idx === -1) {
      extras += 1;
    } else {
      matched.add(idx);
      const ev = events.find((e) => e.index === idx);
      offsets.push(p.timeMs - ev.startMs);
    }
  }
  const noteEvents = events.filter((e) => e.kind === "note");
  const statuses = {};
  for (const ev of noteEvents) statuses[ev.index] = matched.has(ev.index) ? "hit" : "miss";
  const total = noteEvents.length;
  const hits = matched.size;
  return {
    total,
    hits,
    misses: total - hits,
    extras,
    accuracy: total ? hits / total : 0,
    avgOffsetMs: offsets.length
      ? Math.round(offsets.reduce((a, b) => a + b, 0) / offsets.length)
      : 0,
    statuses,
  };
}

// ---------------------------------------------------------------------------
// MIDI-record quantizer — a played take → text-format lines
// ---------------------------------------------------------------------------

// notes: [{ midi, timeMs, durMs? }] with timeMs relative to the start of
// recording (after any count-in). Monophonic. When durMs (held length) is
// present it sets the note's length and any gap to the next onset becomes
// rests; without it a note sustains legato until the next one starts.
// grid is in quarter-note beats: 0.5 = eighths, 0.25 = sixteenths.
export function quantizeTake(notes, opts) {
  const { bpm, tonicMidi, grid = 0.5, beatsPerBar = 4 } = opts;
  const beatMs = 60000 / bpm;
  const snap = (ms) => Math.round(ms / beatMs / grid) * grid;

  const sorted = [...notes].sort((a, b) => a.timeMs - b.timeMs);
  const placed = [];
  for (let i = 0; i < sorted.length; i++) {
    const start = snap(sorted[i].timeMs);
    const nextStart = i + 1 < sorted.length ? snap(sorted[i + 1].timeMs) : null;
    // Simultaneous-after-snap notes collapse to the later one (monophonic).
    if (nextStart !== null && nextStart === start) continue;
    let end;
    if (sorted[i].durMs) {
      end = Math.max(start + grid, snap(sorted[i].timeMs + sorted[i].durMs));
    } else if (nextStart !== null) {
      end = nextStart;
    } else {
      end = start + 1;
    }
    if (nextStart !== null && end > nextStart) end = nextStart;
    placed.push({
      start,
      beats: end - start,
      pitch: serializePitch(midiToDegree(sorted[i].midi, tonicMidi)),
    });
  }

  // Emit tokens with rests filling the gaps and barlines on bar boundaries.
  const tokens = [];
  let cursor = 0;
  const pushBarlinesTo = (target) => {
    // Insert "|" whenever cursor sits exactly on a bar boundary before target.
    while (cursor < target) {
      if (cursor > 0 && cursor % beatsPerBar === 0 && tokens[tokens.length - 1] !== "|")
        tokens.push("|");
      // Fill gap to the next boundary (or target) with rests.
      const nextBoundary = Math.floor(cursor / beatsPerBar) * beatsPerBar + beatsPerBar;
      const stop = Math.min(target, nextBoundary);
      let gap = stop - cursor;
      while (gap > 0.001) {
        const chunk = Math.min(gap, 1);
        for (const t of beatsToTokens("0", chunk)) tokens.push(t);
        gap -= chunk;
        cursor += chunk;
      }
      cursor = stop;
    }
    if (cursor > 0 && cursor % beatsPerBar === 0 && tokens[tokens.length - 1] !== "|")
      tokens.push("|");
  };

  for (const note of placed) {
    pushBarlinesTo(note.start);
    // The base token can straddle a bar boundary (the editor's bar-length
    // warnings guide cleanup), but dashes are standalone tokens, so a bar
    // line slots in between them when a held note crosses bars.
    const noteTokens = beatsToTokens(note.pitch, note.beats);
    tokens.push(noteTokens[0]);
    cursor = note.start + note.beats - (noteTokens.length - 1);
    for (let k = 1; k < noteTokens.length; k++) {
      if (cursor % beatsPerBar === 0 && tokens[tokens.length - 1] !== "|") tokens.push("|");
      tokens.push("-");
      cursor += 1;
    }
    if (cursor % beatsPerBar === 0 && tokens[tokens.length - 1] !== "|") tokens.push("|");
  }

  // Group into lines of ~4 bars for readability.
  const lines = [];
  let current = [];
  let barsInLine = 0;
  for (const t of tokens) {
    current.push(t);
    if (t === "|") {
      barsInLine += 1;
      if (barsInLine >= 4) {
        lines.push(current.join(" "));
        current = [];
        barsInLine = 0;
      }
    }
  }
  if (current.length) lines.push(current.join(" "));
  return lines.join("\n");
}
